// Sous-échantillonnage : filtre passe-bas (deux biquads en cascade, ordre 4) puis
// conservation d'un échantillon sur N. Le piano n'a pas besoin de 48 kHz pour la
// détection de hauteur ; analyser vers 16 kHz divise le coût de calcul par trois.

class Biquad {
  private b0 = 1;
  private b1 = 0;
  private b2 = 0;
  private a1 = 0;
  private a2 = 0;
  private z1 = 0;
  private z2 = 0;

  static lowpass(sampleRate: number, cutoff: number, q = Math.SQRT1_2): Biquad {
    const f = new Biquad();
    const w0 = (2 * Math.PI * cutoff) / sampleRate;
    const cos = Math.cos(w0);
    const alpha = Math.sin(w0) / (2 * q);
    const a0 = 1 + alpha;
    f.b0 = (1 - cos) / 2 / a0;
    f.b1 = (1 - cos) / a0;
    f.b2 = (1 - cos) / 2 / a0;
    f.a1 = (-2 * cos) / a0;
    f.a2 = (1 - alpha) / a0;
    return f;
  }

  process(x: number): number {
    const y = this.b0 * x + this.z1;
    this.z1 = this.b1 * x - this.a1 * y + this.z2;
    this.z2 = this.b2 * x - this.a2 * y;
    return y;
  }
}

export class Decimator {
  readonly factor: number;
  readonly outputRate: number;
  private readonly stages: Biquad[];
  private counter = 0;

  constructor(inputRate: number, factor: number) {
    this.factor = Math.max(1, Math.floor(factor));
    this.outputRate = inputRate / this.factor;
    const cutoff = 0.4 * this.outputRate;
    this.stages = this.factor > 1 ? [Biquad.lowpass(inputRate, cutoff), Biquad.lowpass(inputRate, cutoff)] : [];
  }

  /** Nombre d'échantillons d'entrée à lire avant le prochain échantillon conservé (0 = le prochain est conservé). */
  get phase(): number {
    return (this.factor - this.counter) % this.factor;
  }

  process(input: Float32Array): Float32Array {
    if (this.factor === 1) return input;
    const out = new Float32Array(Math.ceil((input.length + this.counter) / this.factor));
    let n = 0;
    for (let i = 0; i < input.length; i++) {
      let x = input[i];
      for (const s of this.stages) x = s.process(x);
      if (this.counter === 0) out[n++] = x;
      this.counter = (this.counter + 1) % this.factor;
    }
    return n === out.length ? out : out.subarray(0, n);
  }
}

/** Choisit un facteur entier pour ramener le taux d'analyse près de la cible. */
export function chooseDecimationFactor(inputRate: number, targetRate: number): number {
  return Math.max(1, Math.round(inputRate / targetRate));
}
